import { environment } from '../../../environments/environment';

// A dev-only test harness, NOT a product feature — see README.md in this folder.
//
// Requirement: two gates are required to turn it on, a non-production build and
// an explicit scenario in localStorage, and a production build cannot turn it on
// at all. Deliberately not `DemoModeService`: "safe to show a customer" and
// "safe to fake a card charge" must not share a switch.

export const BLUESAGE_MOCK_RAIL_LS_KEY = 'bluesage-mock-rail';

const BLUESAGE_MOCK_RAIL_QUERY_PARAM = 'mockRail';

export const DEFAULT_MOCK_LATENCY_MS = 350;

export const MOCK_PAYMENT_TYPE_ID = 'mock-config-payment-type-bluesage';

export const MOCK_LOCATION_ID = 'bsp_loc_mock';

// `refuse` and `network` are not interchangeable: the server closes a row whose
// charge or push was refused and leaves one whose outcome is unknown pending, and
// the mock answers the dialog's cancel the same way. `refuse` produces Apollo
// `graphQLErrors`, `network` a `networkError`.
export type MockCallResult = 'ok' | 'refuse' | 'network';

export type MockPushOutcome =
    | 'completed'
    | 'declined'
    | 'cancelled'
    | 'timeout'
    | 'failed'
    | 'lost';

export interface MockTerminal {
    epi: string;
    status: 'online' | 'offline' | 'unknown';
    busyJobCode?: string;
}

export interface MockCardOnFile {
    token: string;
    cardBrand: string;
    last4: string;
}

export interface MockPushBehaviour {
    accept: MockCallResult;
    // Defaults to false because that is production behaviour: the push mutations
    // return before the device answers and carry no push id, so the panel follows the
    // push through its transaction. True exercises the older poller, still live code.
    pushIdOnCreate?: boolean;
    pollsBeforeSettle?: number;
    outcome: MockPushOutcome;
    failure?: string;
}

export interface BlueSageMockScenario {
    label: string;
    latencyMs?: number;

    // Omitted and empty are different, here and on `cardsOnFile` and `push`: omitted
    // takes the working default so a scenario states only what it changes, and an
    // empty array is the deliberate "this location has none".
    terminals?: MockTerminal[];
    cardsOnFile?: MockCardOnFile[];

    clientToken?: MockCallResult | 'no-embed-url';
    charge?: MockCallResult;
    payLink?: MockCallResult;
    refund?: MockCallResult;

    push?: MockPushBehaviour;
}

export const DEFAULT_MOCK_TERMINALS: MockTerminal[] = [
    { epi: '2233445566', status: 'online' },
    { epi: '9988776655', status: 'online' },
];

export const DEFAULT_MOCK_CARDS: MockCardOnFile[] = [
    { token: 'bsp_pp_mock_visa', cardBrand: 'Visa', last4: '4242' },
    { token: 'bsp_pp_mock_mc', cardBrand: 'Mastercard', last4: '8210' },
];

export const DEFAULT_MOCK_PUSH: MockPushBehaviour = {
    accept: 'ok',
    pollsBeforeSettle: 2,
    outcome: 'completed',
};

export const BLUESAGE_MOCK_SCENARIOS: Record<string, BlueSageMockScenario> = {

    happy: {
        label: 'Everything works — 2 cards on file, 2 online terminals, push approves',
    },

    'no-cards': {
        label: 'No cards on file — the Card on file tile has nothing behind it',
        cardsOnFile: [],
    },

    'no-terminals': {
        label: 'No terminals on the location at all',
        terminals: [],
    },

    'card-declined': {
        label: 'Card DECLINED by the server — the pending row must be discarded',
        charge: 'refuse',
    },

    'card-network-error': {
        label: 'Charge request never completed — the pending row must be KEPT',
        charge: 'network',
    },

    'card-form-fails': {
        label: 'Client token mints with no embed URL — Key in card cannot open a form',
        clientToken: 'no-embed-url',
    },

    'terminal-offline': {
        label: 'One offline, one busy, one unknown; the push is refused outright',
        terminals: [
            { epi: '2233445566', status: 'offline' },
            { epi: '9988776655', status: 'online', busyJobCode: 'MOCK-1042' },
            { epi: '1122334455', status: 'unknown' },
        ],
        push: { accept: 'refuse', outcome: 'failed', failure: 'That terminal is offline.' },
    },

    'push-declined': {
        label: 'Push reaches the device and is DECLINED — the row must be cancelled',
        push: { accept: 'ok', pollsBeforeSettle: 2, outcome: 'declined' },
    },

    'push-cancelled': {
        label: 'Push cancelled at the device — nothing taken, the row must be cancelled',
        push: { accept: 'ok', pollsBeforeSettle: 2, outcome: 'cancelled' },
    },

    'push-timeout': {
        label: 'Push TIMES OUT — nothing is known, so the row must be left pending',
        push: { accept: 'ok', pollsBeforeSettle: 3, outcome: 'timeout' },
    },

    'push-refused-after-start': {
        label: 'Push accepted, then the status read reports it failed',
        push: {
            accept: 'ok',
            pollsBeforeSettle: 2,
            outcome: 'failed',
            failure: 'The terminal rejected the amount.',
        },
    },

    'push-slow': {
        label: 'Push sits on the device for ~30 s before settling — the waiting UI',
        push: { accept: 'ok', pollsBeforeSettle: 15, outcome: 'completed' },
    },

    'push-lost': {
        label: 'The CRM loses sight of the push — the panel gives it up after ~30 s',
        push: { accept: 'ok', pollsBeforeSettle: 2, outcome: 'lost' },
    },

    'push-inline': {
        label: 'Push settles on its own response, carrying a push id (the mock-rail shape)',
        push: { accept: 'ok', pollsBeforeSettle: 0, outcome: 'completed' },
    },

    'paylink-refused': {
        label: 'Pay link cannot be minted — the create fails as a whole',
        payLink: 'refuse',
    },

    'refund-refused': {
        label: 'Refund and hold release are refused by the rail',
        refund: 'refuse',
    },

    slow: {
        label: 'Everything works, every call takes 2.5 s — spinners and double-click guards',
        latencyMs: 2500,
    },
};

// Requirement: a production build must never reach the interception path, so
// this is the gate the link checks before anything else.
export function isMockRailAvailable(): boolean {
    return !environment.production;
}

function hydrateScenarioFromQueryParam(): void {
    try {
        const params = new URLSearchParams(window.location.search);
        const requested = params.get(BLUESAGE_MOCK_RAIL_QUERY_PARAM);
        if (requested === null) { return; }

        if (requested === '' || requested === 'off') {
            localStorage.removeItem(BLUESAGE_MOCK_RAIL_LS_KEY);
            return;
        }

        if (BLUESAGE_MOCK_SCENARIOS[requested]) {
            localStorage.setItem(BLUESAGE_MOCK_RAIL_LS_KEY, requested);
        }
    } catch {
        // A harness that cannot read its own flag is off.
    }
}

let hydrated = false;

export function activeMockScenarioName(): string | null {
    if (!isMockRailAvailable()) { return null; }

    if (!hydrated) {
        hydrated = true;
        hydrateScenarioFromQueryParam();
    }

    try {
        const name = localStorage.getItem(BLUESAGE_MOCK_RAIL_LS_KEY);
        return name && BLUESAGE_MOCK_SCENARIOS[name] ? name : null;
    } catch {
        return null;
    }
}

export function activeMockScenario(): BlueSageMockScenario | null {
    const name = activeMockScenarioName();
    return name ? BLUESAGE_MOCK_SCENARIOS[name] : null;
}
